import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Users, Phone, MessageCircle, Search, Star, Download, Utensils, Bike } from 'lucide-react';
import { fmtDateTimeIST } from '../../lib/tz';


const FORM_LABEL = {
  dinein: { text: 'Dine-in', cls: 'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20', Icon: Utensils },
  delivery: { text: 'Delivery', cls: 'bg-[#D4A017]/15 text-[#8A6900] border-[#D4A017]/30', Icon: Bike },
};

export default function ContactsTab() {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [outletFilter, setOutletFilter] = useState('all');
  const [formTypeFilter, setFormTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then(r => setOutlets(r.data)).catch(() => {});
  }, [API, getHeaders]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (outletFilter !== 'all') params.set('outlet_id', outletFilter);
    if (formTypeFilter !== 'all') params.set('form_type', formTypeFilter);
    if (search.trim()) params.set('search', search.trim());
    return params;
  }, [outletFilter, formTypeFilter, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/feedback/contacts?${buildParams()}`,
        { headers: getHeaders() },
      );
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [API, getHeaders, buildParams]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/admin/feedback/contacts/export?${buildParams()}`,
        { headers: getHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `customer_contacts_${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="contacts-tab">
      {/* Filters */}
      <Card className="border-[#E2E8DF]">
        <CardContent className="py-3 px-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-[#5C6B64] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search phone or name…"
              data-testid="contacts-search"
              className="h-9 pl-8 text-sm" />
          </div>
          <Select value={outletFilter} onValueChange={setOutletFilter}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="contacts-outlet-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outlets</SelectItem>
              {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={formTypeFilter} onValueChange={setFormTypeFilter}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="contacts-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="dinein">Dine-in</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[11px] text-[#5C6B64]" data-testid="contacts-count">
            {items.length} contact{items.length !== 1 ? 's' : ''}
          </span>
          <Button
            onClick={handleDownload}
            disabled={downloading || items.length === 0}
            size="sm"
            variant="outline"
            className="h-9 ml-auto"
            data-testid="contacts-download-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {downloading ? 'Preparing…' : 'Download CSV'}
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <Users className="w-4 h-4 text-[color:var(--color-primary)]" />
            Customer contacts
          </CardTitle>
          <p className="text-xs text-[#5C6B64]">
            Built from QR-feedback submissions. Dine-in and Delivery are tracked as separate contact rows.
          </p>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B64]">No customer contacts yet</p>
              <p className="text-xs text-[#5C6B64] mt-0.5">Customers who submit feedback with a phone number will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-y border-[#E2E8DF]">
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Outlet</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-center">Visits</th>
                    <th className="px-3 py-2 text-center">Last rating</th>
                    <th className="px-3 py-2 text-left">Last feedback</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c, i) => {
                    const ratingCls = c.last_rating == null ? 'text-[#5C6B64]'
                      : c.last_rating >= 4 ? 'text-[#2D6A4F]'
                      : c.last_rating >= 3 ? 'text-[#D4A017]' : 'text-[#C45A41]';
                    const typeMeta = FORM_LABEL[c.form_type];
                    return (
                      <tr key={c.id || i} className="border-b border-[#E2E8DF]/60 hover:bg-[#F9F9F8]"
                        data-testid={`contact-row-${c.phone_number}-${c.form_type || 'legacy'}`}>
                        <td className="px-3 py-2 font-mono tabular-nums text-xs">{c.phone_number}</td>
                        <td className="px-3 py-2 text-xs text-[#1A2421]">{c.name || <span className="text-[#5C6B64] italic">Anonymous</span>}</td>
                        <td className="px-3 py-2 text-xs text-[#1A2421]">{c.outlet_name || '—'}</td>
                        <td className="px-3 py-2">
                          {typeMeta ? (
                            <Badge variant="outline" className={`text-[10px] px-2 py-0 border ${typeMeta.cls}`}>
                              <typeMeta.Icon className="w-2.5 h-2.5 mr-1" />
                              {typeMeta.text}
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-[#5C6B64]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-xs font-semibold text-[#1A2421]">{c.visit_count || 0}</td>
                        <td className={`px-3 py-2 text-center text-xs font-bold tabular-nums ${ratingCls}`}>
                          {c.last_rating != null ? (
                            <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3" fill="currentColor" /> {c.last_rating}</span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#5C6B64]">
                          {c.last_seen_at ? fmtDateTimeIST(c.last_seen_at) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <a href={`tel:+91${c.phone_number}`}
                              title="Call"
                              data-testid={`contact-call-${c.phone_number}`}
                              className="w-7 h-7 rounded-full bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                              <Phone className="w-3 h-3" />
                            </a>
                            <a href={`https://wa.me/91${c.phone_number}`} target="_blank" rel="noopener noreferrer"
                              title="WhatsApp"
                              data-testid={`contact-whatsapp-${c.phone_number}`}
                              className="w-7 h-7 rounded-full bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors">
                              <MessageCircle className="w-3 h-3" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
